/**
 * useChatSession Hook
 *
 * Manages chat session state, messages, and API interactions.
 * Integrates with progress tracking for real-time updates.
 *
 * @param {string} userId - User ID
 * @param {object} context - Context object (reportContext, subjectContext, etc.)
 * @param {function} onTitleUpdated - Callback when title is updated
 * @param {object} apiClient - API client with session methods (default: ApiReportsChatbot)
 * @param {function} progressTrackingHook - Progress tracking hook (default: useReportsProgressTracking)
 * @param {string} welcomeMessage - Custom welcome message (optional)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import ApiReportsChatbot from '../../../../api/reports-chatbot';
import ApiSubjectManagementAi from '../../../../api/subjectManagementAi';
import { useReportsProgressTracking } from './useReportsProgressTracking';
import { parseContextTag, validateContextTag } from '../../../../utils/contextTagParser';
import { resolveSubject } from '../../../../utils/subjectResolver';

export function useChatSession(
  userId,
  context = null,
  onTitleUpdated = null,
  apiClient = ApiReportsChatbot, // Default API for session management (create, list, load)
  progressTrackingHook = useReportsProgressTracking,
  welcomeMessage = null,
  sessionSource = null, // 'reports' | 'subject-management' - for context type detection
  // NEW: Secondary API clients for dynamic routing
  reportsApiClient = ApiReportsChatbot,
  subjectApiClient = ApiSubjectManagementAi
) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [error, setError] = useState(null);

  const abortControllerRef = useRef(null);
  const loadingTimerRef = useRef(null);
  const contextRef = useRef(context);
  const onTitleUpdatedRef = useRef(onTitleUpdated);
  const titleGeneratedForSessionRef = useRef(new Set()); // Track which sessions have had titles generated
  const isInitializingRef = useRef(false); // Track if initialization is in progress

  // Keep context ref updated
  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  // Keep onTitleUpdated ref updated
  useEffect(() => {
    onTitleUpdatedRef.current = onTitleUpdated;
  }, [onTitleUpdated]);

  // Reset initialization flag when userId changes
  useEffect(() => {
    isInitializingRef.current = false;
  }, [userId]);

  // Progress tracking
  const { stageName, elapsed } = progressTrackingHook(currentRequestId);

  const elapsedSeconds = Math.max(
    elapsed ? elapsed / 1000 : 0,
    loadingDuration || 0
  );

  // Loading timer
  useEffect(() => {
    if (loading && loadingStartTime) {
      loadingTimerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = (now - loadingStartTime) / 1000;
        setLoadingDuration(elapsed);
      }, 100);
    } else {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
    };
  }, [loading, loadingStartTime]);

  /**
   * Initialize new session (backend reuses last empty session if exists)
   */
  const initializeSession = useCallback(async () => {
    if (!userId) {
      return;
    }

    // Prevent duplicate initialization (e.g., from React StrictMode double-mounting)
    if (isInitializingRef.current) {
      return;
    }

    // Mark as initializing BEFORE the async call starts
    isInitializingRef.current = true;

    try {
      // Backend will check for empty sessions and reuse if exists
      const cleanUserId = typeof userId === 'object' ? String(userId?.id || userId) : userId;
      const response = await apiClient.createSession(cleanUserId, 'New Analysis Session', null, {
        purpose: 'reports-assistant'
      });

      const session = response.data && response.data.data;
      if (session) {
        setSessionId(session.id);

        // Show welcome message if provided
        if (welcomeMessage) {
          setMessages([{
            role: 'assistant',
            content: welcomeMessage,
            timestamp: new Date(),
            isGreeting: true
          }]);
        }
      }
    } catch (err) {
      console.error('Failed to initialize session:', err);
      setError('Failed to create chat session');
    } finally {
      isInitializingRef.current = false;
    }
  }, [userId]);

  /**
   * Load specific session by ID
   */
  const loadSession = useCallback(async (sessionIdToLoad) => {
    if (!sessionIdToLoad) {
      setSessionId(null);
      setMessages([]);
      return;
    }

    try {
      setSessionId(sessionIdToLoad);

      // Load messages
      const messagesResponse = await apiClient.getMessages(sessionIdToLoad);
      const loadedMessages = (messagesResponse.data && messagesResponse.data.data && messagesResponse.data.data.messages) || [];

      // Filter out greeting messages and map to correct format
      const formattedMessages = loadedMessages
        .filter(msg => !msg.isGreeting)
        .map(msg => ({
          role: msg.role || (msg.dataValues && msg.dataValues.role),
          content: msg.content || (msg.dataValues && msg.dataValues.content),
          timestamp: new Date(msg.createdAt || (msg.dataValues && msg.dataValues.createdAt)),
          metadata: msg.metadata || (msg.dataValues && msg.dataValues.metadata)
        }));

      setMessages(formattedMessages);
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Failed to load session');
    }
  }, []);

  /**
   * Create new session
   */
  const createNewSession = useCallback(async (title = 'Reports Analysis Session') => {
    if (!userId) return null;

    try {

      // Ensure userId is a clean value (not a React element or object)
      const cleanUserId = typeof userId === 'object' ? String(userId?.id || userId) : userId;

      const response = await apiClient.createSession(cleanUserId, title);
      const newSessionId = response.data && response.data.data && response.data.data.id;
      setSessionId(newSessionId);
      setMessages([]);
      setError(null);
      return newSessionId;
    } catch (err) {
      console.error('Failed to create session:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError('Failed to create new session');
      return null;
    }
  }, [userId]);

  /**
   * Send message to AI
   */
  const sendMessage = useCallback(async (messageContent, files = []) => {
    if (!messageContent.trim() && files.length === 0) return;
    if (loading) return;

    const userMessage = messageContent.trim();
    setError(null);

    // STEP 1: Parse context tag
    const parsedTag = parseContextTag(userMessage);
    console.log('[useChatSession] Parsed tag:', {
      userMessage,
      parsedTag
    });
    const validation = validateContextTag(parsedTag);

    if (!validation.valid) {
      setError(validation.error);
      return; // Block message if tag is invalid
    }

    // STEP 2: Resolve context
    let resolvedContext = null;

    console.log('[useChatSession] Resolving context for parsed tag:', {
      hasTag: parsedTag.hasTag,
      contextType: parsedTag.contextType,
      identifier: parsedTag.identifier
    });

    if (parsedTag.hasTag) {
      // Explicit tag - resolve new context
      if (parsedTag.contextType === 'reports') {
        resolvedContext = {
          type: 'reports',
          reportContext: contextRef.current,
          subjectContext: null,
          identifier: null,
          resolvedSubject: null
        };
      } else if (parsedTag.contextType === 'subject') {
        if (parsedTag.identifier) {
          // Resolve specific subject
          const subject = await resolveSubject(
            parsedTag.identifier,
            contextRef.current?.projectId
          );

          if (!subject) {
            // SHOW ERROR AND BLOCK
            setError(
              `Could not find subject "${parsedTag.identifier}". ` +
              `Please check the email or ID.`
            );
            return;
          }

          resolvedContext = {
            type: 'subject',
            reportContext: null,
            subjectContext: {
              ...contextRef.current,
              selectedSubjectId: subject.id,
              subjectEmail: subject.email,
              subjectDisplayId: subject.displayId
            },
            identifier: parsedTag.identifier,
            resolvedSubject: subject
          };
        } else {
          // General subject management context
          resolvedContext = {
            type: 'subject',
            reportContext: null,
            subjectContext: contextRef.current,
            identifier: null,
            resolvedSubject: null
          };
        }
      }
    } else {
      // No tag - inherit from previous message or session source
      const lastMessage = messages[messages.length - 1];
      const lastContext = lastMessage?.metadata?.contextType;

      if (lastContext) {
        // Continue with previous context
        resolvedContext = {
          type: lastContext,
          reportContext: lastContext === 'reports' ? contextRef.current : null,
          subjectContext: lastContext === 'subject' ? contextRef.current : null,
          identifier: lastMessage?.metadata?.contextIdentifier,
          resolvedSubject: lastMessage?.metadata?.resolvedSubject
        };
      } else {
        // Default to session source or route-based detection
        const defaultType = sessionSource === 'subject-management'
          ? 'subject'
          : 'reports';

        resolvedContext = {
          type: defaultType,
          reportContext: defaultType === 'reports' ? contextRef.current : null,
          subjectContext: defaultType === 'subject' ? contextRef.current : null,
          identifier: null,
          resolvedSubject: null
        };
      }
    }

    // STEP 3: Ensure we have a session
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      try {

        // Ensure userId is a clean value (not a React element or object)
        const cleanUserId = typeof userId === 'object' ? String(userId?.id || userId) : userId;

        const response = await apiClient.createSession(cleanUserId, 'Analysis Session');
        currentSessionId = response.data && response.data.data && response.data.data.id;
        setSessionId(currentSessionId);
      } catch (err) {
        console.error('Failed to create session:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError('Failed to start chat session');
        return;
      }
    }

    // STEP 4: Build message metadata
    const messageMetadata = {
      contextType: resolvedContext.type,
      contextIdentifier: resolvedContext.identifier,
      resolvedSubjectId: resolvedContext.resolvedSubject?.id,
      resolvedSubjectEmail: resolvedContext.resolvedSubject?.email,
      resolvedSubjectDisplayId: resolvedContext.resolvedSubject?.displayId,
      hadContextTag: parsedTag.hasTag,
      originalMessage: parsedTag.originalMessage
    };

    // STEP 5: Add user message to UI
    const newUserMessage = {
      role: 'user',
      content: parsedTag.cleanMessage, // Message without tag
      timestamp: new Date(),
      metadata: {
        ...messageMetadata,
        ...(files.length > 0 && {
          attachments: files.map(f => ({ fileName: f.name, type: f.type, size: f.size }))
        })
      }
    };
    setMessages(prev => [...prev, newUserMessage]);

    // STEP 6: Start loading
    setLoading(true);
    setLoadingStartTime(Date.now());
    setLoadingDuration(0);

    // Create request ID for progress tracking
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setCurrentRequestId(requestId);

    // Create AbortController
    abortControllerRef.current = new AbortController();

    try {
      // Select appropriate context for API
      const apiContextToSend = resolvedContext.type === 'reports'
        ? resolvedContext.reportContext
        : resolvedContext.subjectContext;

      const contextMetadataToSend = {
        contextType: resolvedContext.type,
        explicitSwitch: parsedTag.hasTag,
        identifier: resolvedContext.identifier,
        resolvedSubjectId: resolvedContext.resolvedSubject?.id,
        resolvedSubjectEmail: resolvedContext.resolvedSubject?.email,
        resolvedSubjectDisplayId: resolvedContext.resolvedSubject?.displayId
      };

      // DYNAMIC API CLIENT SELECTION based on context type
      const activeApiClient = resolvedContext.type === 'reports'
        ? reportsApiClient
        : subjectApiClient;

      // Branch API call based on whether files are attached
      let response;
      const messageToSend = parsedTag.cleanMessage || (files.length > 0 ? 'Please analyze the attached file(s).' : '');
      console.log('[useChatSession] API call:', {
        hasFiles: files.length > 0,
        filesCount: files.length,
        files: files.map(f => ({ name: f.name, type: f.type, size: f.size })),
        messageToSend: messageToSend.substring(0, 100),
        contextType: resolvedContext.type,
        apiClient: resolvedContext.type === 'reports' ? 'reportsApiClient' : 'subjectApiClient',
        endpoint: files.length > 0 ? 'sendMessageWithAttachments' : 'sendMessage',
        sessionId: currentSessionId
      });
      if (files.length > 0) {
        response = await activeApiClient.sendMessageWithAttachments(
          currentSessionId,
          messageToSend,
          userId,
          apiContextToSend,
          files,
          {
            maxRecentMessages: 10,
            requestId,
            signal: abortControllerRef.current.signal,
            contextMetadata: contextMetadataToSend
          }
        );
      } else {
        response = await activeApiClient.sendMessage(
          currentSessionId,
          messageToSend,
          userId,
          apiContextToSend,
          {
            maxRecentMessages: 10,
            requestId,
            signal: abortControllerRef.current.signal,
            contextMetadata: contextMetadataToSend
          }
        );
      }

      const assistantResponse = response.data && response.data.data && response.data.data.assistantMessage;

      if (assistantResponse) {
        // Notify parent to refresh session list (for re-sorting by lastActivity)
        if (onTitleUpdatedRef.current) {
          onTitleUpdatedRef.current(currentSessionId);
        }

        setMessages(prev => {
          const updatedMessages = [...prev, {
            role: 'assistant',
            content: assistantResponse.content,
            timestamp: new Date(assistantResponse.createdAt),
            metadata: assistantResponse.metadata
          }];

          // Count non-greeting messages to determine if this is the first exchange
          const nonGreetingMessages = updatedMessages.filter(msg => !msg.isGreeting);
          const userMessages = nonGreetingMessages.filter(msg => msg.role === 'user');

          // Auto-generate title after first real exchange (1 user message + 1 assistant response)
          if (userMessages.length === 1 && nonGreetingMessages.length === 2) {
            // Check if we've already generated a title for this session
            if (titleGeneratedForSessionRef.current.has(currentSessionId)) {
              return updatedMessages;
            }

            // Mark this session as having title generation in progress
            titleGeneratedForSessionRef.current.add(currentSessionId);

            // Add small delay to ensure messages are committed to database
            setTimeout(() => {
              // Generate title in background (non-blocking) using the active API client
              activeApiClient.generateTitle(currentSessionId, userId)
                .then(titleResponse => {
                  const newTitle = titleResponse.data && titleResponse.data.data && titleResponse.data.data.title;

                  // Notify parent to refresh session list
                  if (onTitleUpdatedRef.current) {
                    onTitleUpdatedRef.current(currentSessionId, newTitle);
                  }
                })
                .catch(err => {
                  console.error('[AutoTitle] Failed to auto-generate title:', err);
                  // Remove from set if failed so it can be retried
                  titleGeneratedForSessionRef.current.delete(currentSessionId);
                });
            }, 500); // 500ms delay - reduced from 1 second
          }

          return updatedMessages;
        });
      }

    } catch (err) {
      if (err.name === 'CanceledError' || err.message === 'canceled') {
        console.log('Request was cancelled');
      } else {
        console.error('Failed to send message:', err);
        const errorMessage = (err.response && err.response.data && err.response.data.error) || 'Failed to get response from AI';
        setError(errorMessage);
        setMessages(prev => [...prev, {
          role: 'system',
          content: `Error: ${(err.response && err.response.data && err.response.data.error) || 'Failed to get response. Please try again.'}`,
          timestamp: new Date(),
          isError: true
        }]);
      }
    } finally {
      setLoading(false);
      setLoadingStartTime(null);
      setCurrentRequestId(null);
      abortControllerRef.current = null;
    }
  }, [userId, sessionId, loading, messages, sessionSource, reportsApiClient, subjectApiClient]);

  /**
   * Cancel current request
   */
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setLoading(false);
      setCurrentRequestId(null);
    }
  }, []);

  /**
   * Delete current session
   */
  const deleteSession = useCallback(async () => {
    if (!sessionId || !userId) return;

    try {
      await apiClient.deleteSession(sessionId, userId, true);
      setSessionId(null);
      setMessages([]);
      await initializeSession();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete session');
    }
  }, [sessionId, userId, initializeSession]);

  return {
    sessionId,
    messages,
    loading,
    error,
    setError,
    stageName,
    elapsedSeconds,
    initializeSession,
    loadSession,
    createNewSession,
    sendMessage,
    cancelRequest,
    deleteSession
  };
}

export default useChatSession;
